import { redact } from './redact';

import type { ErrorEvent, MonitoringProperties } from './types';

const redactProperties = (
  properties: MonitoringProperties | undefined,
): MonitoringProperties | undefined => {
  if (!properties) {
    return undefined;
  }
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [
      key,
      typeof value === 'string' ? redact(value) : value,
    ]),
  );
};

/**
 * Turns a thrown value into a redacted {@link ErrorEvent}.
 *
 * The single place redaction is applied to errors we observe, so a sink can
 * assume its input is safe to transmit. A sink whose SDK collects errors
 * independently never sees them through here and must redact that path itself.
 *
 * `throw 'a string'` and `Promise.reject({})` are both legal and both reach the
 * global handlers, so this accepts `unknown` and never throws.
 */
export const toErrorEvent = (
  value: unknown,
  properties?: MonitoringProperties,
  options?: { fatal?: boolean },
): ErrorEvent => {
  const fatal = options?.fatal ?? true;

  if (value instanceof Error) {
    // Read `.stack` before redacting the message. V8 formats it lazily and
    // caches the header on first read, so reading it here — rather than
    // mutating the Error and hoping — is what makes the redaction reliable.
    const rawStack = typeof value.stack === 'string' ? value.stack : undefined;

    return {
      name: redact(value.name || 'Error'),
      message: redact(value.message),
      stack: rawStack ? redact(rawStack) : undefined,
      fatal,
      properties: redactProperties(properties),
    };
  }

  return {
    name: 'NonError',
    message: redact(value),
    fatal,
    properties: redactProperties(properties),
  };
};
